/* import __COLOCATED_TEMPLATE__ from './conversation-funnel.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type { Options, SeriesOptionsType, SeriesSankeyOptions, Point } from 'highcharts';
import { use } from 'ember-resources/util/function-resource';
import { FinPerformanceFunnelConfig } from 'embercom/components/reporting/bespoke/fin-performance/conversation-funnel/series-config';
import PALETTE from '@intercom/pulse/lib/palette';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import type { SeriesSankeyPointOptionsObject } from 'highcharts';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
import type RenderableChart from 'embercom/models/reporting/custom/renderable-chart';

export const SANKEY_COLORS = {
  channelType: PALETTE['vis-blue-40'],
  finInvolved: PALETTE['vis-blue-50'],
  finResolved: PALETTE['vis-mint-50'],
  deadEnd: PALETTE['text-disabled'],
  positiveCSAT: PALETTE['vis-violet-50'],
  neutralCSAT: PALETTE['vis-yellow-50'],
  negativeCSAT: PALETTE['vis-salmon-50'],
  default: PALETTE['neutral-border-emphasis'],
  procedureHandoff: PALETTE['vis-orange-50'],
  rulesAndGuidance: PALETTE['vis-slateblue-50'],
  userRequested: PALETTE['vis-pink-50'],
};

const CHANNEL_TYPES = [
  'Chat',
  'Email',
  'Phone',
  'Android',
  'iOS',
  'Twitter',
  'FacebookMessenger',
  'Facebook',
  'WhatsApp',
  'Instagram',
  'PhoneSwitch',
  'SMS',
  'ZendeskTicket',
  'SalesforceCase',
  'ZendeskMessaging',
  'Slack',
  'Unknown',
];

function nodeDataLabelIsHidden(node: any) {
  return node.dataLabel.element.attributes.opacity?.value === '0';
}

const increasedPadding = 35;

function adjustPaddingIfDataLabelsOverlap(series: any) {
  if (series.nodes.some(nodeDataLabelIsHidden)) {
    series.update({ nodePadding: increasedPadding }, false);
  }
}

type OptionsWithSankey = Options & { plotOptions: { sankey: { borderRadius: number } } };

function safelyAdjustLayout(series: any, adjust: (series: any) => void) {
  if (!series.adjustingLayout) {
    series.adjustingLayout = true;
    // because layout adjustments can trigger a redraw, we need to prevent infinite recursion
    adjust(series);
    series.redraw();
    series.adjustingLayout = false;
  }
}

const bottomPadding = 50;

function pushNodesToBottom(series: any) {
  let nodes = series.nodes;
  if (!nodes) {
    return;
  }

  let chart = series.chart;
  let plotHeight = chart.plotHeight;

  let nodesToPush = nodes.filter(
    (node: any) => node.pushToBottom && node.offsetVertical === undefined,
  );

  if (nodesToPush.length === 0) {
    return;
  }

  let currentBottomY = plotHeight - bottomPadding;

  nodesToPush.reverse().forEach((node: any) => {
    let renderedNode = nodes.find((rNode: any) => rNode.id === node.id);
    if (!renderedNode) {
      return;
    }
    let nodeY = renderedNode.nodeY;
    let nodeHeight = renderedNode.shapeArgs?.height || 0;

    let offsetVertical = currentBottomY - nodeY - nodeHeight;
    node.update({ offsetVertical }, false);

    currentBottomY = currentBottomY - nodeHeight - 30;
  });
}

function adjustNodeLayout(event: Highcharts.PointerEventObject) {
  let chart = event.target as any;
  let series = chart.series[0];

  safelyAdjustLayout(series, () => {
    // push nodes with pushToBottom flag to the bottom of the chart
    pushNodesToBottom(series);
    // it's important to adjust the padding last because the other changes
    // can influence the data label positions
    adjustPaddingIfDataLabelsOverlap(series);
  });
}

function stripZeroWeights(
  data: SeriesSankeyPointOptionsObject[],
): SeriesSankeyPointOptionsObject[] {
  return data.filter((point) => point.weight !== 0);
}

interface Signature {
  renderableChart: RenderableChart;
  openUnderlyingDataModal: (chartSeries: ChartSeries) => void;
}

interface FunnelPoint extends Point {
  title?: string;
  findByLabel?: string;
  weight?: number;
  count?: number;
}

export default class ConversationFunnel extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked tooltipOpenerElement: HTMLElement | undefined = undefined;
  @tracked hoveredPoint: FunnelPoint | null = null;

  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.sankeyChartDefinition,
    reportState: () => this.args.renderableChart.reportState,
  });

  get highchartsSeries() {
    return [this.sankeySeries] as SeriesOptionsType[];
  }

  get useSankeyFilterUpdate() {
    return this.appService.app.canUseFeature('sankey-filter-update');
  }

  get sankeyChartDefinition() {
    return {
      chart_series: FinPerformanceFunnelConfig.getChartSeriesWithLabels(
        this.intl,
        this.useSankeyFilterUpdate,
      ),
      view_by: null,
    };
  }

  get highchartsOptions(): OptionsWithSankey {
    let component = this;
    // a sankey chart doesn't have axes, or a legend
    // we are adding some dummy series so we can show stuff in the chart legend
    // but having other series will enable the axes, so we need to hide them
    return {
      xAxis: {
        visible: false,
      },
      yAxis: {
        visible: false,
      },
      tooltip: {
        enabled: false,
      },
      chart: {
        marginRight: 0,
        marginTop: 20,
        animation: false, // disables animations on redraw (but not initial draw)
        events: {
          redraw: adjustNodeLayout.bind(this),
        },
      },
      plotOptions: {
        sankey: {
          point: {
            events: {
              mouseOver(this: Point, event: PointerEvent) {
                component.hoveredPoint = this;
                component.tooltipOpenerElement = (event.target as any).graphic.element;
                return false;
              },
              mouseOut(this: Point, _event: PointerEvent) {
                component.tooltipOpenerElement = undefined;
                return false;
              },
              click(this: Point, _event: PointerEvent) {
                component.hoveredPoint = this;
                component.onViewConversations();
                return false;
              },
            },
          },
          cursor: 'pointer',
          nodeWidth: 6,
          borderRadius: 3,
          nodePadding: 8,
          nodeAlignment: 'top',
          linkOpacity: 1.0,
          states: {
            inactive: {
              // this is really for the node data labels. The opacity for the links and nodes cannot be set separately
              // here, but they can be controlled via the styles defined in the hbs file
              opacity: 1,
              linkOpacity: 1.0,
            },
          },
          minLinkWidth: 3,
          dataLabels: {
            padding: 0,
            color: 'black',
            useHTML: true,
            // crop: false & overflow: allow tell highcharts that it's okay to render the labels in the margins
            crop: false,
            overflow: 'allow',
            allowOverlap: false, // we detect if labels are hidden and increase padding
            enabled: true,
            zIndex: 10,
            // see https://www.highcharts.com/docs/chart-concepts/templating
            nodeFormat: `
            <div class="flex flex-col text-default text-caption">
              <div class="flex flex-row font-mono uppercase leading-4">
                {point.title}
              </div>
              <div class="flex flex-row mt-1">
                <div class="p-1 rounded text-data-detail {point.textClass}" style="background-color: {point.color}">
                  {point.sum:,.0f}
                </div>
              </div>
            </div>
          `,
          },
          levels: [
            {
              level: 0,
              dataLabels: {
                x: 20,
                align: 'left',
              },
            },
            {
              level: 1,
              dataLabels: {
                x: 20,
                align: 'left',
              },
            },
            {
              level: 2,
              dataLabels: {
                x: 20,
                align: 'left',
              },
            },
            {
              level: 3,
              dataLabels: {
                x: -20,
                align: 'right',
                nodeFormat: `
                <div class="flex flex-col text-default text-caption">
                  <div class="flex flex-row font-mono uppercase leading-4 justify-end">
                    {point.title}
                  </div>
                  <div class="flex flex-row justify-end mt-1">
                    <div class="p-1 rounded text-data-detail {point.textClass}" style="background-color: {point.color}">
                      {point.sum:,.0f}
                    </div>
                  </div>
                </div>
                `,
              },
            },
          ],
        },
      },
    };
  }

  get metrics() {
    return FinPerformanceFunnelConfig.getMetricsFromChartDataRequest(
      this.dataResource.chartData,
      this.useSankeyFilterUpdate,
    );
  }

  get hasData() {
    let topOfFunnel = this.metrics.conversations;
    return !!topOfFunnel;
  }

  get channelTypeNodes() {
    return CHANNEL_TYPES.map((channelType) => ({
      id: channelType,
      title: this.intl.t(`ai-agent.analyze.performance.funnel-metrics.${channelType}`),
      count: this.metrics[channelType as keyof typeof this.metrics],
      color: SANKEY_COLORS.channelType,
      textClass: 'text-on-neutral',
    }));
  }

  get finInvolvedNodes() {
    return [
      {
        id: 'FinInvolved',
        title: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinInvolved'),
        count: this.metrics.FinInvolved,
        color: SANKEY_COLORS.finInvolved,
        textClass: 'text-on-neutral',
      },
      {
        id: 'FinNotInvolved',
        title: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinNotInvolved'),
        count: this.metrics.FinNotInvolved,
        color: SANKEY_COLORS.deadEnd,
        textClass: 'text-on-neutral',
      },
    ];
  }

  get finResolutionNodes() {
    return [
      {
        id: 'FinResolved',
        title: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinResolved'),
        count: this.metrics.FinResolved,
        color: SANKEY_COLORS.finResolved,
        textClass: 'text-default',
      },
      {
        id: 'FinProcedureHandoff',
        title: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinProcedureHandoff'),
        count: this.metrics.FinProcedureHandoff,
        color: SANKEY_COLORS.procedureHandoff,
        textClass: 'text-on-neutral',
      },
      {
        id: 'FinAndTeammate',
        title: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinAndTeammate'),
        count: this.metrics.FinAndTeammate,
        color: SANKEY_COLORS.deadEnd,
        textClass: 'text-on-neutral',
      },
      {
        id: 'FinAbandoned',
        title: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinAbandoned'),
        count: this.metrics.FinAbandoned,
        color: SANKEY_COLORS.default,
        textClass: 'text-default',
      },
      ...(this.useSankeyFilterUpdate
        ? [
            {
              id: 'FinEscalationRulesAndGuidance',
              title: this.intl.t(
                'ai-agent.analyze.performance.funnel-metrics.FinEscalationRulesAndGuidance',
              ),
              count: this.metrics.FinEscalationRulesAndGuidance,
              color: SANKEY_COLORS.rulesAndGuidance,
              textClass: 'text-on-neutral',
              pushToBottom: true,
            },
            {
              id: 'FinEscalationUserRequested',
              title: this.intl.t(
                'ai-agent.analyze.performance.funnel-metrics.FinEscalationUserRequested',
              ),
              count: this.metrics.FinEscalationUserRequested,
              color: SANKEY_COLORS.userRequested,
              textClass: 'text-on-neutral',
              pushToBottom: true,
            },
          ]
        : []),
    ];
  }

  get csatNodes() {
    return [
      {
        id: 'positiveCSAT',
        title: this.intl.t('ai-agent.analyze.performance.funnel-metrics.PositiveCSAT'),
        findByLabel: this.intl.t(
          'ai-agent.analyze.performance.funnel-metrics.FinResolvedAndPositiveCSAT',
        ),
        count: this.metrics.FinResolvedAndPositiveCSAT,
        color: SANKEY_COLORS.positiveCSAT,
        textClass: 'text-on-neutral',
      },
      {
        id: 'neutralCSAT',
        title: this.intl.t('ai-agent.analyze.performance.funnel-metrics.NeutralCSAT'),
        findByLabel: this.intl.t(
          'ai-agent.analyze.performance.funnel-metrics.FinResolvedAndNeutralCSAT',
        ),
        count: this.metrics.FinResolvedAndNeutralCSAT,
        color: SANKEY_COLORS.neutralCSAT,
        textClass: 'text-default',
      },
      {
        id: 'negativeCSAT',
        title: this.intl.t('ai-agent.analyze.performance.funnel-metrics.NegativeCSAT'),
        findByLabel: this.intl.t(
          'ai-agent.analyze.performance.funnel-metrics.FinResolvedAndNegativeCSAT',
        ),
        count: this.metrics.FinResolvedAndNegativeCSAT,
        color: SANKEY_COLORS.negativeCSAT,
        textClass: 'text-on-neutral',
      },
    ];
  }

  get channelTypeToFinInvolvedNodes() {
    return this.channelTypeNodes
      .map((channelTypeNode) => {
        return this.finInvolvedNodes.map((finInvolvedNode) => ({
          from: channelTypeNode.id,
          to: finInvolvedNode.id,
          name: this.intl.t(
            `ai-agent.analyze.performance.funnel-metrics.${channelTypeNode.id}And${finInvolvedNode.id}`,
          ),
          weight:
            this.metrics[
              `${channelTypeNode.id}And${finInvolvedNode.id}` as keyof typeof this.metrics
            ],
        }));
      })
      .flat();
  }

  get finInvolvedToResolutionNodes() {
    return [
      {
        from: 'FinInvolved',
        to: 'FinResolved',
        name: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinInvolvedAndFinResolved'),
        findByLabel: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinResolved'),
        weight: this.metrics.FinResolved,
      },
      {
        from: 'FinInvolved',
        to: 'FinProcedureHandoff',
        name: this.intl.t(
          'ai-agent.analyze.performance.funnel-metrics.FinInvolvedAndFinProcedureHandoff',
        ),
        findByLabel: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinProcedureHandoff'),
        weight: this.metrics.FinProcedureHandoff,
      },
      {
        from: 'FinInvolved',
        to: 'FinAndTeammate',
        name: this.intl.t(
          'ai-agent.analyze.performance.funnel-metrics.FinInvolvedAndFinAndTeammate',
        ),
        findByLabel: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinAndTeammate'),
        weight: this.metrics.FinAndTeammate,
      },
      {
        from: 'FinInvolved',
        to: 'FinAbandoned',
        name: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinInvolvedAndFinAbandoned'),
        findByLabel: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinAbandoned'),
        weight: this.metrics.FinAbandoned,
      },
      ...(this.useSankeyFilterUpdate
        ? [
            {
              from: 'FinAndTeammate',
              to: 'FinEscalationRulesAndGuidance',
              name: this.intl.t(
                'ai-agent.analyze.performance.funnel-metrics.FinEscalationRulesAndGuidance',
              ),
              findByLabel: this.intl.t(
                'ai-agent.analyze.performance.funnel-metrics.FinEscalationRulesAndGuidance',
              ),
              weight: this.metrics.FinEscalationRulesAndGuidance,
            },
            {
              from: 'FinAndTeammate',
              to: 'FinEscalationUserRequested',
              name: this.intl.t(
                'ai-agent.analyze.performance.funnel-metrics.FinEscalationUserRequested',
              ),
              findByLabel: this.intl.t(
                'ai-agent.analyze.performance.funnel-metrics.FinEscalationUserRequested',
              ),
              weight: this.metrics.FinEscalationUserRequested,
            },
          ]
        : []),
    ];
  }

  get finResolutionToCsatNodes() {
    return [
      {
        from: 'FinResolved',
        to: 'positiveCSAT',
        name: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinResolvedAndPositiveCSAT'),
        weight: this.metrics.FinResolvedAndPositiveCSAT,
      },
      {
        from: 'FinResolved',
        to: 'neutralCSAT',
        name: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinResolvedAndNeutralCSAT'),
        weight: this.metrics.FinResolvedAndNeutralCSAT,
      },
      {
        from: 'FinResolved',
        to: 'negativeCSAT',
        name: this.intl.t('ai-agent.analyze.performance.funnel-metrics.FinResolvedAndNegativeCSAT'),
        weight: this.metrics.FinResolvedAndNegativeCSAT,
      },
    ];
  }

  // TODO: localize these when the strings are finalized
  /* eslint-disable @intercom/intercom/no-bare-strings */
  get sankeySeries(): SeriesSankeyOptions {
    let nodes = [
      ...this.channelTypeNodes,
      ...this.finInvolvedNodes,
      ...this.finResolutionNodes,
      ...this.csatNodes,
    ];
    let data = stripZeroWeights([
      ...this.channelTypeToFinInvolvedNodes,
      ...this.finInvolvedToResolutionNodes,
      ...this.finResolutionToCsatNodes,
    ]);
    return {
      type: 'sankey',
      linkColorMode: 'gradient',
      animation: false,
      custom: {
        // this is used in the data label template (plotOptions.sankey.nodeFormat)
        total: this.metrics.conversations,
      },
      nodes,
      data,
    };
  }

  @action
  onViewConversations() {
    if (!this.hoveredPoint) {
      return;
    }

    let metricName =
      this.hoveredPoint.findByLabel || this.hoveredPoint.title || this.hoveredPoint.name;
    let chartSeries = this.args.renderableChart.chartSeries.toArray().findBy('label', metricName);
    this.args.openUnderlyingDataModal(chartSeries);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::ConversationFunnel': typeof ConversationFunnel;
    'reporting/bespoke/fin-performance/conversation-funnel': typeof ConversationFunnel;
  }
}
